import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary, Alert, LinearProgress, Typography } from "@mui/material";
import { isAxiosError } from "axios";
import uniq from "lodash/uniq";
import type { FC } from "react";
import { useMemo } from "react";
import { useAgents } from "../../admin/agents/useAgents";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import type { CustomerContactPerson } from "../customersApi";
import { useCustomerNotes } from "../notes/useCustomerNotes";
import { CallRecordingsList } from "./CallRecordingsList";
import CreateCallRecordingNoteDialog from "./CreateCallRecordingNoteDialog";
import { normalizePhoneNumber } from "./phoneNumber";
import { useCallRecordingNoteDraft } from "./useCallRecordingNoteDraft";
import { useCallRecordingPlayback } from "./useCallRecordingPlayback";
import { useCallRecordingRows } from "./useCallRecordingRows";
import { useCallRecordings } from "./useCallRecordings";

interface Props {
  readonly contacts: CustomerContactPerson[];
  readonly customerId: string;
}

interface ContentProps {
  readonly numbers: string[];
  readonly contacts: CustomerContactPerson[];
  readonly customerId: string;
}

const CallRecordingsContent: FC<ContentProps> = ({ numbers, contacts, customerId }) => {
  const { recordings, isLoading, error } = useCallRecordings(numbers);
  const { agents } = useAgents();
  const { addNote } = useCustomerNotes(customerId, "PRZEZMORZE");

  const { audioRef, playingRecId, loadingRecId, handlePlayToggle } = useCallRecordingPlayback();
  const { generatingNoteRecId, noteDraftRecId, noteDraftText, handleCreateNoteDraft, closeDraft } =
    useCallRecordingNoteDraft();

  const rows = useCallRecordingRows(recordings, contacts, agents);

  const handleSaveNoteDraft = async (recId: string, editedText: string) => {
    const row = rows.find((r) => r.recId === recId);
    await addNote({
      text: editedText,
      type: "Telefon",
      category: "PRZEZMORZE",
      date: row ? dayjs(row.startTime).format(ISO_DATE_FORMAT) : undefined,
    });
  };

  const isNotConfigured = isAxiosError(error) && error.response?.status === 501;

  return (
    <>
      {isLoading && <LinearProgress data-testid="loading-indicator" />}
      {isNotConfigured && <Alert severity="info">Funkcja nagrań rozmów nie jest jeszcze skonfigurowana.</Alert>}
      {error && !isNotConfigured && <Alert severity="error">Nie udało się pobrać listy rozmów.</Alert>}
      {!error && (
        <CallRecordingsList
          rows={rows}
          playingRecId={playingRecId}
          loadingRecId={loadingRecId}
          generatingNoteRecId={generatingNoteRecId}
          onPlayToggle={handlePlayToggle}
          onCreateNoteDraft={handleCreateNoteDraft}
        />
      )}
      <audio ref={audioRef} hidden />
      {noteDraftRecId !== null && (
        <CreateCallRecordingNoteDialog
          draftText={noteDraftText}
          customerId={customerId}
          readOnlyCustomer={true}
          onClose={closeDraft}
          onSave={(editedText) => handleSaveNoteDraft(noteDraftRecId, editedText)}
        />
      )}
    </>
  );
};

const CallRecordings: FC<Props> = ({ contacts, customerId }) => {
  const numbers = useMemo(
    () =>
      uniq(
        contacts
          .map((contact) => contact.phone)
          .filter((phone): phone is string => !!phone)
          .map(normalizePhoneNumber),
      ),
    [contacts],
  );

  if (numbers.length === 0) {
    return null;
  }

  return (
    <Accordion slotProps={{ transition: { unmountOnExit: true, mountOnEnter: true } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">Historia rozmów telefonicznych</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <CallRecordingsContent numbers={numbers} contacts={contacts} customerId={customerId} />
      </AccordionDetails>
    </Accordion>
  );
};

export default CallRecordings;
